import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { api } from '../lib/api';
import { formatVnd, formatDateTime } from '../lib/format';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { EmptyState } from '../components/ui/empty-state';
import { Skeleton } from '../components/ui/skeleton';
import { BulkActionBar } from '../components/BulkActionBar';
import { OperationResultStack, type OperationResult } from '../components/OperationResultStack';
import { ConfirmDialog, type ConfirmDialogProps } from '../components/ConfirmDialog';
import { RuleReferenceDialog } from '../components/RuleReferenceDialog';
import { useRowSelection } from '../hooks/useRowSelection';

const SEVERITIES = ['', 'high', 'medium', 'low'] as const;
const RULE_CODES = ['', 'R1', 'R2', 'R3', 'R6', 'R8', 'R9'];

export function AlertsPage() {
  const qc = useQueryClient();
  const [severity, setSeverity] = useState<string>('');
  const [ruleCode, setRuleCode] = useState<string>('');
  const { data, isLoading } = useQuery({
    queryKey: ['alerts', severity, ruleCode],
    queryFn: () =>
      api.listAlerts({ severity: severity || undefined, rule_code: ruleCode || undefined }),
  });
  const rows = data?.rows ?? [];
  const totalMatching = data?.total_count ?? rows.length;
  const resetKey = `${severity}|${ruleCode}`;
  const { selectedIds, allOnPage, toggle, toggleAllOnPage, clear } = useRowSelection(
    rows,
    resetKey,
  );
  const [opResults, setOpResults] = useState<OperationResult[]>([]);
  const [confirm, setConfirm] = useState<Omit<ConfirmDialogProps, 'open' | 'onCancel'> | null>(
    null,
  );

  function pushResult(res: OperationResult) {
    setOpResults((p) => [res, ...p].slice(0, 5));
  }

  const bulkAck = useMutation({
    mutationFn: (ids: number[]) => api.bulkAcknowledgeAlerts(ids),
    onSuccess: (res) => {
      pushResult({
        key: `bulk-ack-${Date.now()}`,
        kind: 'success',
        title: `Đã acknowledge ${res.affected} alert`,
      });
      clear();
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ['alerts'] });
      qc.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (e) =>
      pushResult({
        key: `bulk-ack-err-${Date.now()}`,
        kind: 'failure',
        title: 'Bulk acknowledge fail',
        detail: String(e).slice(0, 200),
      }),
  });
  const bulkDismiss = useMutation({
    mutationFn: (ids: number[]) => api.bulkDismissAlerts(ids),
    onSuccess: (res) => {
      pushResult({
        key: `bulk-dismiss-${Date.now()}`,
        kind: 'success',
        title: `Đã dismiss ${res.affected} alert`,
      });
      clear();
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ['alerts'] });
      qc.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (e) =>
      pushResult({
        key: `bulk-dismiss-err-${Date.now()}`,
        kind: 'failure',
        title: 'Bulk dismiss fail',
        detail: String(e).slice(0, 200),
      }),
  });

  return (
    <div className="space-y-4 pb-24">
      <PageHeader
        title="Open alerts"
        actions={
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <RuleReferenceDialog />
            <label className="flex items-center gap-2">
              <span className="text-muted-foreground">Severity:</span>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="rounded border border-border bg-card p-1 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s || 'All'}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-muted-foreground">Rule:</span>
              <select
                value={ruleCode}
                onChange={(e) => setRuleCode(e.target.value)}
                className="rounded border border-border bg-card p-1 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {RULE_CODES.map((r) => (
                  <option key={r} value={r}>
                    {r || 'All'}
                  </option>
                ))}
              </select>
            </label>
          </div>
        }
      />

      <OperationResultStack
        items={opResults}
        onDismiss={(k) => setOpResults((p) => p.filter((r) => r.key !== k))}
      />

      {isLoading && <Skeleton className="h-40 w-full" />}

      {data && rows.length === 0 && (
        <EmptyState icon={ShieldCheck} title="No open alerts" description="No open alerts. 🎉" />
      )}

      {data && rows.length > 0 && (
        <>
          <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={allOnPage}
              onChange={toggleAllOnPage}
              className="h-5 w-5 cursor-pointer accent-primary"
              aria-label="Chọn tất cả trang"
            />
            <span className="text-muted-foreground">Chọn tất cả ({rows.length} dòng)</span>
          </div>
          <div className="space-y-3">
            {rows.map((a) => {
              const sevVariant =
                a.severity === 'high'
                  ? 'destructive'
                  : a.severity === 'medium'
                    ? 'warning'
                    : 'info';
              const borderBg =
                a.severity === 'high'
                  ? 'border-destructive/30 bg-destructive/10'
                  : a.severity === 'medium'
                    ? 'border-warning/30 bg-warning/10'
                    : 'border-border bg-card';
              const isSelected = selectedIds.has(a.id);
              return (
                <div
                  key={a.id}
                  className={`rounded-lg border p-4 transition-shadow hover:shadow-md ${borderBg} ${
                    isSelected ? 'ring-1 ring-primary' : ''
                  }`}
                >
                  <div className="mb-2 flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(a.id)}
                      className="mt-0.5 h-5 w-5 cursor-pointer accent-primary"
                      aria-label={`Chọn alert #${a.id}`}
                    />
                    <div className="flex-1">
                      <div className="mb-2 flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <Badge variant={sevVariant}>{a.severity}</Badge>
                          <span className="font-mono text-muted-foreground">{a.rule_code}</span>
                          {a.status === 'acknowledged' && (
                            <span className="text-xs text-success">
                              ✓ Acknowledged by {a.acknowledged_by_email}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground/70">
                          {formatDateTime(a.created_at)}
                        </span>
                      </div>
                      <div className="mb-2 text-sm text-foreground">{a.summary}</div>
                      <div className="text-xs text-muted-foreground">
                        {a.card_alias} · {a.merchant_canonical ?? '—'} ·{' '}
                        <span className="tabular-nums">{formatVnd(a.amount_vnd)}</span>
                      </div>
                      <div className="mt-2 text-right">
                        <Link
                          to={`/transactions/${a.transaction_id}`}
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          Open transaction
                          <ChevronRight size={14} strokeWidth={1.5} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <BulkActionBar
        selectedCount={selectedIds.size}
        totalMatching={totalMatching}
        isAllOnPageSelected={allOnPage}
        filterMode={false}
        onEscalateToFilter={() => {}}
        onClear={clear}
      >
        <Button
          variant="default"
          onClick={() => {
            const ids = [...selectedIds];
            setConfirm({
              title: `Acknowledge ${ids.length} alert?`,
              description: `Các alert sẽ chuyển sang trạng thái "acknowledged" — vẫn lưu, không còn hiển thị mặc định ở queue.`,
              confirmLabel: 'Acknowledge',
              tone: 'primary',
              pending: bulkAck.isPending,
              onConfirm: () => bulkAck.mutate(ids),
            });
          }}
          disabled={bulkAck.isPending || bulkDismiss.isPending}
        >
          {bulkAck.isPending ? 'Đang…' : 'Acknowledge'}
        </Button>
        <Button
          variant="destructive"
          onClick={() => {
            const ids = [...selectedIds];
            setConfirm({
              title: `Dismiss ${ids.length} alert?`,
              description: `${ids.length} alert sẽ chuyển sang trạng thái "dismissed" và biến mất khỏi mọi danh sách. Không thể undo.`,
              confirmLabel: 'Dismiss',
              tone: 'destructive',
              pending: bulkDismiss.isPending,
              onConfirm: () => bulkDismiss.mutate(ids),
            });
          }}
          disabled={bulkDismiss.isPending || bulkAck.isPending}
        >
          {bulkDismiss.isPending ? 'Đang…' : 'Dismiss'}
        </Button>
      </BulkActionBar>

      <ConfirmDialog
        open={confirm != null}
        title={confirm?.title ?? ''}
        description={confirm?.description ?? ''}
        confirmLabel={confirm?.confirmLabel ?? 'OK'}
        tone={confirm?.tone}
        pending={confirm?.pending}
        onConfirm={() => confirm?.onConfirm()}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
